/**
 * Call Tracking Utility
 * Manages call cadence, metrics, and automatic lead status updates
 * 
 * Call Cadence Rules:
 * - 1st call: Within 1 hour of lead submission (during working hours)
 * - 2nd call: 2 hours after 1st attempt (if no answer)
 * - 3rd call: Next business day
 * - 4th call: 3 days later
 * - After 4 unanswered calls in a week: Mark lead as lost
 */

import { Activity, Lead, CallOutcome, LeadCallMetrics, CallCadenceConfig } from '../types';
import { collection, query, where, getDocs, orderBy, Timestamp, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

// Default call cadence configuration
export const DEFAULT_CALL_CADENCE: CallCadenceConfig = {
  intervals: [1, 2, 24, 72], // 1hr, 2hr, 1 day, 3 days (in hours)
  maxAttempts: 4,
  workingHoursStart: 9, // 9 AM
  workingHoursEnd: 17, // 5 PM
  workingDays: [1, 2, 3, 4, 5], // Monday to Friday
};

/**
 * Get the call cadence configuration from localStorage or use defaults
 */
export function getCallCadenceConfig(): CallCadenceConfig {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_CALL_CADENCE;
  }
  
  try {
    const saved = localStorage.getItem('crm_automation_settings');
    if (saved) {
      const settings = JSON.parse(saved);
      return {
        intervals: [
          settings.firstCallInterval || 1,
          settings.secondCallInterval || 2,
          settings.thirdCallInterval || 24,
          settings.fourthCallInterval || 72,
        ],
        maxAttempts: settings.maxAttempts || 4,
        workingHoursStart: settings.workingHoursStart ?? 9,
        workingHoursEnd: settings.workingHoursEnd ?? 17,
        workingDays: settings.workingDays || [1, 2, 3, 4, 5],
      };
    }
  } catch (e) {
    console.error('Error reading automation settings:', e);
  }
  
  return DEFAULT_CALL_CADENCE;
}

/**
 * Check if auto-create initial call is enabled
 */
export function isAutoCreateInitialCallEnabled(): boolean {
  if (typeof localStorage === 'undefined') {
    return true; // Default to enabled
  }
  
  try {
    const saved = localStorage.getItem('crm_automation_settings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.autoCreateInitialCall !== false; // Default to true
    }
  } catch (e) {
    console.error('Error reading automation settings:', e);
  }
  
  return true;
}

// Call outcomes that count as "unanswered"
const UNANSWERED_OUTCOMES: CallOutcome[] = ['no_answer', 'voicemail', 'busy'];

/**
 * Check if current time is within working hours
 */
export function isWithinWorkingHours(
  date: Date = new Date(),
  config: CallCadenceConfig = DEFAULT_CALL_CADENCE
): boolean {
  const hours = date.getHours();
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  return (
    config.workingDays.includes(dayOfWeek) &&
    hours >= config.workingHoursStart &&
    hours < config.workingHoursEnd
  );
}

/**
 * Get the next working hours datetime
 */
export function getNextWorkingTime(
  fromDate: Date = new Date(),
  config: CallCadenceConfig = DEFAULT_CALL_CADENCE
): Date {
  const result = new Date(fromDate);
  
  // If within working hours, return current time
  if (isWithinWorkingHours(result, config)) {
    return result;
  }
  
  // Move to next working period
  const hours = result.getHours();
  
  // If before working hours today, set to start of working hours
  if (hours < config.workingHoursStart && config.workingDays.includes(result.getDay())) {
    result.setHours(config.workingHoursStart, 0, 0, 0);
    return result;
  }
  
  // Move to next day and find next working day
  result.setDate(result.getDate() + 1);
  result.setHours(config.workingHoursStart, 0, 0, 0);
  
  while (!config.workingDays.includes(result.getDay())) {
    result.setDate(result.getDate() + 1);
  }
  
  return result;
}

/**
 * Calculate the next call time based on attempt number
 */
export function calculateNextCallTime(
  lastCallTime: Date | null,
  attemptNumber: number,
  leadCreatedAt: Date,
  config: CallCadenceConfig = DEFAULT_CALL_CADENCE
): Date | null {
  // If max attempts reached, no next call
  if (attemptNumber >= config.maxAttempts) {
    return null;
  }
  
  let baseTime: Date;
  
  if (attemptNumber === 0) {
    // First call - within 1 hour of lead creation
    baseTime = new Date(leadCreatedAt);
    baseTime.setHours(baseTime.getHours() + config.intervals[0]);
  } else if (lastCallTime) {
    // Subsequent calls - based on interval from last call
    const intervalHours = config.intervals[Math.min(attemptNumber, config.intervals.length - 1)];
    baseTime = new Date(lastCallTime);
    baseTime.setHours(baseTime.getHours() + intervalHours);
  } else {
    return null;
  }
  
  // Adjust to next working hours if needed
  return getNextWorkingTime(baseTime, config);
}

/**
 * Get call metrics for a lead based on their activities
 */
export async function getLeadCallMetrics(
  leadId: string,
  leadCreatedAt: Date,
  config: CallCadenceConfig = DEFAULT_CALL_CADENCE
): Promise<LeadCallMetrics> {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  // Fetch call activities for this lead
  const activitiesRef = collection(db, 'activities');
  const q = query(
    activitiesRef,
    where('entityId', '==', leadId),
    where('entityType', '==', 'lead'),
    where('type', '==', 'call'),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  const callActivities = snapshot.docs.map(doc => ({
    ...doc.data(),
    id: doc.id,
    completedAt: doc.data().completedAt?.toDate?.() || new Date(doc.data().completedAt),
  })) as Activity[];
  
  // Calculate metrics
  const callHistory = callActivities.map(activity => ({
    date: activity.completedAt || new Date(),
    outcome: (activity.outcome as CallOutcome) || 'no_answer',
    notes: activity.description,
  }));
  
  // Count calls in the past week
  const recentCalls = callHistory.filter(call => call.date >= oneWeekAgo);
  const unansweredInWeek = recentCalls.filter(call => 
    UNANSWERED_OUTCOMES.includes(call.outcome)
  ).length;
  
  const totalCalls = callHistory.length;
  const answeredCalls = callHistory.filter(call => call.outcome === 'answered').length;
  const unansweredCalls = callHistory.filter(call => 
    UNANSWERED_OUTCOMES.includes(call.outcome)
  ).length;
  
  const lastCall = callHistory[0];
  const lastCallDate = lastCall?.date;
  const lastCallOutcome = lastCall?.outcome;
  
  // Determine current attempt number (consecutive unanswered calls)
  let consecutiveUnanswered = 0;
  for (const call of callHistory) {
    if (UNANSWERED_OUTCOMES.includes(call.outcome)) {
      consecutiveUnanswered++;
    } else {
      break; // Stop counting when we hit an answered call
    }
  }
  
  // Check if lead should be marked as lost
  const shouldMarkLost = unansweredInWeek >= config.maxAttempts;
  
  // Calculate next call due time
  const nextCallDue = shouldMarkLost 
    ? undefined 
    : calculateNextCallTime(lastCallDate || null, consecutiveUnanswered, leadCreatedAt, config) || undefined;
  
  return {
    totalCalls,
    answeredCalls,
    unansweredCalls,
    lastCallDate,
    lastCallOutcome,
    nextCallDue,
    callAttemptNumber: consecutiveUnanswered + 1,
    shouldMarkLost,
    callHistory,
  };
}

/**
 * Get call metrics from in-memory activities (for real-time updates)
 */
export function calculateCallMetricsFromActivities(
  activities: Activity[],
  leadCreatedAt: Date,
  config: CallCadenceConfig = DEFAULT_CALL_CADENCE
): LeadCallMetrics {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  // Filter to completed call activities, sorted by date descending
  const callActivities = activities
    .filter(a => a.type === 'call' && a.status === 'completed')
    .sort((a, b) => {
      const dateA = a.completedAt || a.createdAt;
      const dateB = b.completedAt || b.createdAt;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  
  const callHistory = callActivities.map(activity => ({
    date: activity.completedAt || activity.createdAt,
    outcome: (activity.outcome as CallOutcome) || 'no_answer',
    notes: activity.description,
  }));
  
  // Count calls in the past week
  const recentCalls = callHistory.filter(call => new Date(call.date) >= oneWeekAgo);
  const unansweredInWeek = recentCalls.filter(call => 
    UNANSWERED_OUTCOMES.includes(call.outcome)
  ).length;
  
  const totalCalls = callHistory.length;
  const answeredCalls = callHistory.filter(call => call.outcome === 'answered').length;
  const unansweredCalls = callHistory.filter(call => 
    UNANSWERED_OUTCOMES.includes(call.outcome)
  ).length;
  
  const lastCall = callHistory[0];
  const lastCallDate = lastCall?.date ? new Date(lastCall.date) : undefined;
  const lastCallOutcome = lastCall?.outcome;
  
  // Count consecutive unanswered calls
  let consecutiveUnanswered = 0;
  for (const call of callHistory) {
    if (UNANSWERED_OUTCOMES.includes(call.outcome)) {
      consecutiveUnanswered++;
    } else {
      break;
    }
  }
  
  const shouldMarkLost = unansweredInWeek >= config.maxAttempts;
  
  const nextCallDue = shouldMarkLost 
    ? undefined 
    : calculateNextCallTime(lastCallDate || null, consecutiveUnanswered, leadCreatedAt, config) || undefined;
  
  return {
    totalCalls,
    answeredCalls,
    unansweredCalls,
    lastCallDate,
    lastCallOutcome,
    nextCallDue,
    callAttemptNumber: consecutiveUnanswered + 1,
    shouldMarkLost,
    callHistory: callHistory.map(c => ({ ...c, date: new Date(c.date) })),
  };
}

/**
 * Log a call activity with outcome
 */
export async function logCallActivity(
  leadId: string,
  leadName: string,
  userId: string,
  outcome: CallOutcome,
  notes?: string
): Promise<string> {
  const now = new Date();
  
  const activityData = {
    type: 'call',
    title: `Call - ${getOutcomeLabel(outcome)}`,
    description: notes || `Call outcome: ${getOutcomeLabel(outcome)}`,
    entityType: 'lead',
    entityId: leadId,
    entityName: leadName,
    status: 'completed',
    outcome: outcome,
    completedAt: Timestamp.fromDate(now),
    createdBy: userId,
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now),
  };
  
  const docRef = await addDoc(collection(db, 'activities'), activityData);
  return docRef.id;
}

/**
 * Check and update lead status if they should be marked as lost
 */
export async function checkAndUpdateLeadStatus(
  leadId: string,
  metrics: LeadCallMetrics
): Promise<boolean> {
  if (metrics.shouldMarkLost) {
    const leadRef = doc(db, 'leads', leadId);
    await updateDoc(leadRef, {
      status: 'lost',
      updatedAt: Timestamp.fromDate(new Date()),
      lostReason: 'No response after 4 call attempts',
    });
    return true;
  }
  return false;
}

/**
 * Get the follow-up interval description based on attempt number
 */
export function getFollowUpDescription(attemptNumber: number, config: CallCadenceConfig = DEFAULT_CALL_CADENCE): string {
  const intervalHours = config.intervals[Math.min(attemptNumber, config.intervals.length - 1)];
  
  if (intervalHours < 24) {
    return `${intervalHours} hour${intervalHours > 1 ? 's' : ''}`;
  } else if (intervalHours === 24) {
    return 'tomorrow';
  } else {
    const days = Math.round(intervalHours / 24);
    return `${days} day${days > 1 ? 's' : ''}`;
  }
}

/**
 * Create a scheduled follow-up call activity
 * Returns the activity ID if created, null if max attempts reached
 */
export function createFollowUpCallData(
  leadId: string,
  leadName: string,
  userId: string,
  attemptNumber: number,
  leadCreatedAt: Date,
  config: CallCadenceConfig = DEFAULT_CALL_CADENCE
): Omit<Activity, 'id' | 'createdAt' | 'updatedAt'> | null {
  // Don't create follow-up if max attempts reached
  if (attemptNumber >= config.maxAttempts) {
    return null;
  }
  
  const nextCallTime = calculateNextCallTime(new Date(), attemptNumber, leadCreatedAt, config);
  if (!nextCallTime) {
    return null;
  }
  
  const followUpDesc = getFollowUpDescription(attemptNumber, config);
  const attemptLabel = attemptNumber + 1; // Human-readable attempt number
  
  return {
    type: 'call',
    title: `Follow-up Call (Attempt ${attemptLabel + 1} of ${config.maxAttempts})`,
    description: `Scheduled follow-up call - ${followUpDesc} after previous attempt`,
    entityType: 'lead',
    entityId: leadId,
    entityName: leadName,
    scheduledAt: nextCallTime,
    dueDate: nextCallTime,
    createdBy: userId,
    assignedTo: userId,
    status: 'scheduled',
  };
}

/**
 * Check if outcome requires a follow-up call
 */
export function requiresFollowUp(outcome: CallOutcome): boolean {
  return UNANSWERED_OUTCOMES.includes(outcome);
}

/**
 * Get human-readable label for call outcome
 */
export function getOutcomeLabel(outcome: CallOutcome): string {
  const labels: Record<CallOutcome, string> = {
    answered: 'Answered',
    no_answer: 'No Answer',
    voicemail: 'Left Voicemail',
    busy: 'Busy',
    wrong_number: 'Wrong Number',
    disconnected: 'Disconnected',
  };
  return labels[outcome] || outcome;
}

/**
 * Get call status indicator for UI
 */
export function getCallStatusIndicator(metrics: LeadCallMetrics): {
  status: 'on_track' | 'due' | 'overdue' | 'lost' | 'contacted';
  label: string;
  color: string;
} {
  if (metrics.shouldMarkLost) {
    return { status: 'lost', label: 'No Response - Lost', color: '#ef4444' };
  }
  
  if (metrics.answeredCalls > 0) {
    return { status: 'contacted', label: 'Contacted', color: '#22c55e' };
  }
  
  if (!metrics.nextCallDue) {
    return { status: 'on_track', label: 'No calls yet', color: '#6b7280' };
  }
  
  const now = new Date();
  const dueTime = new Date(metrics.nextCallDue);
  const hoursDiff = (dueTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  if (hoursDiff < -2) {
    return { 
      status: 'overdue', 
      label: `Call overdue (Attempt ${metrics.callAttemptNumber})`, 
      color: '#ef4444' 
    };
  }
  
  if (hoursDiff < 1) {
    return { 
      status: 'due', 
      label: `Call due now (Attempt ${metrics.callAttemptNumber})`, 
      color: '#f59e0b' 
    };
  }
  
  return { 
    status: 'on_track', 
    label: `Next call: ${formatNextCallTime(dueTime)}`, 
    color: '#3b82f6' 
  };
}

/**
 * Format next call time for display
 */
function formatNextCallTime(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  
  if (diffHours < 1) {
    const diffMins = Math.round(diffMs / (1000 * 60));
    return `in ${diffMins} min`;
  }
  
  if (diffHours < 24) {
    return `in ${Math.round(diffHours)} hours`;
  }
  
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) {
    return 'tomorrow';
  }
  
  return `in ${diffDays} days`;
}

/**
 * Get leads that need calls (for dashboard/alerts)
 */
export function getLeadsNeedingCalls(
  leads: Lead[],
  activitiesByLead: Map<string, Activity[]>
): { lead: Lead; metrics: LeadCallMetrics; urgency: 'overdue' | 'due' | 'upcoming' }[] {
  const results: { lead: Lead; metrics: LeadCallMetrics; urgency: 'overdue' | 'due' | 'upcoming' }[] = [];
  const now = new Date();
  
  for (const lead of leads) {
    // Skip non-active leads
    if (lead.status === 'lost' || lead.archived) {
      continue;
    }
    
    const activities = activitiesByLead.get(lead.id || '') || [];
    const metrics = calculateCallMetricsFromActivities(activities, lead.createdAt);
    
    // Skip if already contacted successfully
    if (metrics.answeredCalls > 0) {
      continue;
    }
    
    // Skip if should be lost (will be handled separately)
    if (metrics.shouldMarkLost) {
      continue;
    }
    
    if (metrics.nextCallDue) {
      const dueTime = new Date(metrics.nextCallDue);
      const hoursDiff = (dueTime.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff < -2) {
        results.push({ lead, metrics, urgency: 'overdue' });
      } else if (hoursDiff < 1) {
        results.push({ lead, metrics, urgency: 'due' });
      } else if (hoursDiff < 4) {
        results.push({ lead, metrics, urgency: 'upcoming' });
      }
    }
  }
  
  // Sort by urgency (overdue first, then due, then upcoming)
  const urgencyOrder = { overdue: 0, due: 1, upcoming: 2 };
  results.sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]);
  
  return results;
}
